import { Link, useNavigate, useLocation, matchPath } from 'react-router-dom'
import { css, useTheme } from '@fable/theme'

import { ListButton } from '../../../list_button/ListButton'
import BookPrice from '../../book_price/BookPrice'
import Dropdown, { DropdownItem } from '../../../../../../components/dropdown'

import { LinkToPath } from '../../../../../../types'
import { BookImage, Button, Icon, Text } from '@fable/components'
import { Book } from '@fable/types'
import {
  useActions,
  useSidebar,
  useTypedSelector,
} from '../../../../../../hooks'
import { routes } from '../../../../../social/routes'
import { BOOK_LIST_SAVE } from '../../../../constants'
import altTag from '../../../../../../utils/altTag'

interface Props {
  book: Book
  customClass?: string
  isAuthenticated: boolean
  isDefaultAction?: boolean
  openBook: () => void
  path: LinkToPath
}

export const RegularBook: React.FC<Props> = ({
  book,
  customClass = '',
  isAuthenticated,
  isDefaultAction,
  openBook,
  path,
}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { onOpenSidebar } = useSidebar()
  const { removeBookFromBookList } = useActions()
  const { bookList } = useTypedSelector(({ books }) => books)
  const { profile } = useTypedSelector(({ user }) => user)
  const { colors } = useTheme()
  const { title, authors, cover_image } = book

  const defaultAction = () => {
    const buttonTitle = 'View book'
    return (
      <Link
        className={css`
          width: 100%;
          max-width: 88px;
        `}
        to={path}
        onClick={(e) => e.stopPropagation()}
      >
        <Button width="100%" label={buttonTitle} small secondary />
      </Link>
    )
  }

  const options: DropdownItem[] = [
    {
      title: 'Save to list',
      icon: <Icon type="star" />,
      action: () => onOpenSidebar(BOOK_LIST_SAVE, { book, bookId: book.id }),
    },
    {
      title: 'Get the book',
      icon: <Icon type="book" />,
      action: () => navigate(path),
    },
    {
      title: 'Remove from list',
      icon: <Icon type="trash" fill={colors.mahogany} />,
      action: () => removeBookFromBookList(bookList.data?.id, book),
      color: colors.mahogany,
    },
  ]

  const renderAction = () => {
    if (!isAuthenticated) return null

    const isBookListPage = matchPath(`${routes.bookList}/:id`, pathname)

    if (bookList.data?.user.id === profile.data?.id && !!isBookListPage)
      return (
        <Dropdown
          items={options}
          controlDirection="horizontal"
          width="253px"
          control
        />
      )

    return <ListButton book={book} />
  }

  return (
    <div
      className={customClass}
      data-testid="regular-book-component"
      onClick={() => navigate(path)}
    >
      <BookImage
        image={cover_image}
        altTag={altTag.storeBook(book)}
        size={130}
      />
      <div className="book-details">
        <div className="top">
          <Text
            className="book-title"
            type="subhead"
            sizing="S"
            tag="h4"
            truncate={2}
          >
            {title}
          </Text>
          <Text
            className="book-author"
            type="body"
            sizing="M"
            color={colors.hurricane}
            truncate={1}
          >
            By {authors[0].name}
          </Text>
        </div>
        <div className="actions">
          {isDefaultAction ? (
            defaultAction()
          ) : (
            <BookPrice
              book={book}
              isAuthenticated={isAuthenticated}
              openBook={openBook}
            />
          )}
          {renderAction()}
        </div>
      </div>
    </div>
  )
}
